import { type TreeNode } from '@direct-frontend/components';

/**
 * Converts a comma-separated string of node IDs to an array of [id, checked] tuples
 * including all children of each ID (marked as unchecked if not in the original string).
 * @param nodeIds Comma-separated string of node IDs
 * @param nodeMap Map of all nodes by ID
 * @returns Array of [id, checked] tuples
 */
/*
 * Converts a comma-separated string of node IDs into an array of checkboxes with their checked status.
 * Each checkbox is represented as a tuple containing the node ID and a boolean indicating whether it is checked.
 * It also processes the children of each node recursively.
 *
 * @param nodeIds A comma-separated string of node IDs that should be checked.
 * @param nodeMap A map where keys are node IDs and values are TreeNode objects representing the nodes.
 * @returns An array of tuples, each containing a node ID and a boolean indicating whether it is checked.
 */
export function nodeIdsToCheckboxes(
    nodeIds: string,
    nodeMap: Map<string, TreeNode>,
    addAllChildren: boolean = false
): [string, boolean][] {
    if (!nodeIds) {
        return [];
    }

    const checkedIdsSet = new Set(nodeIds.split(','));
    const processedIds = new Set<string>();
    const result: [string, boolean][] = [];

    // Process each checked ID and its children
    checkedIdsSet.forEach((id) => {
        if (!processedIds.has(id)) {
            result.push([id, true]);
            processedIds.add(id);
        }

        // Add children recursively
        const processChildren = (nodeId: string) => {
            const node = nodeMap.get(nodeId);

            if (!node?.children) {
                return;
            }

            for (const child of node.children) {
                if (processedIds.has(child.id)) {
                    continue;
                }

                const isChecked = checkedIdsSet.has(child.id);

                result.push([child.id, addAllChildren || isChecked]);
                processedIds.add(child.id);

                processChildren(child.id);
            }
        };

        processChildren(id);
    });

    return result;
}
